import { supabase } from '../lib/supabase';
import { Organisation } from '../types/database';
import { organizationCache, createCacheKey } from '../utils/cacheUtils';

export const organisationService = {
  // Get all organisations with caching
  getAllOrganisations: async () => {
    try {
      const cacheKey = createCacheKey('all_organisations');
      
      // Check cache first
      const cached = organizationCache.get(cacheKey);
      if (cached) {
        console.log('📦 Using cached organizations data');
        return { data: cached, error: null };
      }

      console.log('🔄 Fetching fresh organizations data');
      const { data, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) {
        console.error('Error fetching organisations:', error);
        return { data: [], error };
      }

      const result = data || [];
      organizationCache.set(cacheKey, result);
      console.log('✅ Organizations data cached');
      
      return { data: result, error: null };
    } catch (error) {
      console.error('Error fetching organisations:', error);
      return { data: [], error };
    }
  },

  // Get organisation by ID with caching
  getOrganisationById: async (organisationId: string) => {
    try {
      const cacheKey = createCacheKey('organisation', organisationId);
      
      // Check cache first
      const cached = organizationCache.get(cacheKey);
      if (cached) {
        console.log('📦 Using cached organization data for ID:', organisationId);
        return { data: cached, error: null };
      }

      console.log('🔄 Fetching fresh organization data for ID:', organisationId);
      const { data, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', organisationId)
        .eq('active', true)
        .single();

      if (error) {
        console.error('Error fetching organisation:', error);
        return { data: null, error };
      }

      organizationCache.set(cacheKey, data);
      console.log('✅ Organization data cached for ID:', organisationId);
      
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching organisation:', error);
      return { data: null, error };
    }
  },

  // Get organisation by subdomain
  getOrganisationBySubdomain: async (subdomain: string) => {
    try {
      const { data, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('subdomain', subdomain)
        .eq('active', true)
        .single();

      if (error) {
        console.error('Error fetching organisation by subdomain:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching organisation by subdomain:', error);
      return { data: null, error };
    }
  },

  // Create new organisation
  createOrganisation: async (organisationData: {
    name: string;
    subdomain?: string;
    logo?: string;
  }) => {
    try {
      const { data, error } = await supabase
        .from('organisations')
        .insert({
          ...organisationData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          active: true
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating organisation:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating organisation:', error);
      return { data: null, error };
    }
  },

  // Update organisation
  updateOrganisation: async (organisationId: string, updates: Partial<Organisation>) => {
    try {
      const { data, error } = await supabase
        .from('organisations')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', organisationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating organisation:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating organisation:', error);
      return { data: null, error };
    }
  }
};

export default organisationService;
